import { AuditedProduct } from './scoring-engine-constants';
import { DataModeType, ImportDiagnostics, STORAGE_KEY } from './scorecard-constants';

interface PersistedScorecardState {
  version: 1;
  mode: DataModeType;
  fileName: string;
  products: AuditedProduct[];
}

export const readPersistedScorecardState = (): PersistedScorecardState | null => {
  try {
    const raw = localStorage.getItem(STORAGE_KEY);
    if (!raw) return null;
    const parsed = JSON.parse(raw);
    if (parsed?.version !== 1 || !Array.isArray(parsed.products)) return null;
    return parsed as PersistedScorecardState;
  } catch {
    return null;
  }
};

export const writePersistedScorecardState = (state: PersistedScorecardState): void => {
  try {
    localStorage.setItem(STORAGE_KEY, JSON.stringify(state));
  } catch {
    // Quota exceeded or storage unavailable — in-memory state still works.
  }
};

export const clearPersistedScorecardState = (): void => {
  try {
    localStorage.removeItem(STORAGE_KEY);
  } catch {
    // ignore
  }
};

export interface PortfolioKpis {
  totalCount: number;
  averageScore: number;
  redCount: number;
  amberCount: number;
  greenCount: number;
  uniqueBrandsCount: number;
  missingAplus: number;
  missingVideo: number;
}

export const computePortfolioKpis = (products: AuditedProduct[]): PortfolioKpis => {
  const totalCount = products.length;
  const averageScore =
    totalCount === 0
      ? 0
      : Math.round(products.reduce((acc, p) => acc + p.finalScore, 0) / totalCount);
  const redCount = products.filter(p => p.finalScore < 60).length;
  const amberCount = products.filter(p => p.finalScore >= 60 && p.finalScore <= 79).length;
  const greenCount = products.filter(p => p.finalScore >= 80).length;
  const uniqueBrandsCount = new Set(products.map(p => p.brand).filter(b => b && b !== ' — ')).size;
  const missingAplus = products.filter(p => !p.aplusPresent).length;
  const missingVideo = products.filter(p => p.videosCount === 0).length;

  return {
    totalCount,
    averageScore,
    redCount,
    amberCount,
    greenCount,
    uniqueBrandsCount,
    missingAplus,
    missingVideo
  };
};

export const computeImportDiagnostics = (products: AuditedProduct[]): ImportDiagnostics | null => {
  if (products.length === 0) return null;

  let missingRating = 0;
  let missingBullets = 0;
  let missingDescription = 0;
  let missingTitle = 0;
  let missingAplus = 0;
  let missingVideo = 0;

  for (const p of products) {
    if (p.rating === 0) {
      missingRating++;
    }
    if (p.featureBulletsCount === 0) {
      missingBullets++;
    }
    if (!p.fullDescription || p.fullDescription === ' — ') {
      missingDescription++;
    }
    if (!p.title || p.title === ' — ') {
      missingTitle++;
    }
    if (!p.aplusPresent) {
      missingAplus++;
    }
    if (p.videosCount === 0) {
      missingVideo++;
    }
  }

  return {
    total: products.length,
    missingRating,
    missingBullets,
    missingDescription,
    missingTitle,
    missingAplus,
    missingVideo
  };
};
